import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { FileText, Plus, Trash2, Copy, Download, MessageCircle, Search, Edit3, Printer } from "lucide-react";

const badge = (s) => ({ Paid: "badge-green", Unpaid: "badge-red", "Partially Paid": "badge-yellow" }[s] || "badge-gray");

export default function Invoices({ kind = "invoice" }) {
  const isQuote = kind === "quotation";
  const endpoint = isQuote ? "/quotations" : "/invoices";
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = async () => {
    const params = {}; if (q) params.q = q; if (status) params.status = status;
    const r = await api.get(endpoint, { params });
    setList(r.data);
    if (!isQuote) api.get("/invoices/summary/stats").then((s) => setStats(s.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const downloadPdf = async (item) => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}${endpoint}/${item.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${item.number}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  const del = async (item) => {
    if (!window.confirm(`Delete ${item.number}?`)) return;
    await api.delete(`${endpoint}/${item.id}`); load();
  };

  const duplicate = async (item) => { await api.post(`/invoices/${item.id}/duplicate`); load(); };
  const convertToInvoice = async (item) => { await api.post(`/quotations/${item.id}/convert`); alert(`Converted to invoice`); load(); };

  const whatsappShare = (item) => {
    const msg = encodeURIComponent(`Hi ${item.client_name}, please find your ${isQuote ? "quotation" : "invoice"} ${item.number} for ₹${item.grand_total}. — NAYAN SERVICES`);
    const phone = (item.client_mobile || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const emailShare = (item) => {
    const subject = encodeURIComponent(`${isQuote ? "Quotation" : "Invoice"} ${item.number} from NAYAN SERVICES`);
    const body = encodeURIComponent(`Dear ${item.client_name},\n\nPlease find your ${isQuote ? "quotation" : "invoice"} ${item.number} for ₹${item.grand_total}.\n\nRegards,\nNAYAN SERVICES`);
    window.open(`mailto:${item.client_email || ""}?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid={`${kind}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><FileText size={26} /> {isQuote ? "Quotations" : "Invoices"}</h1>
          <p className="text-sm text-muted-foreground">Create GST {isQuote ? "quotations" : "invoices"}, download PDFs and share instantly</p>
        </div>
        <Link to={isQuote ? "/quotations/new" : "/invoices/new"} className="btn btn-primary" data-testid={`btn-new-${kind}`}><Plus size={16} /> New {isQuote ? "Quotation" : "Invoice"}</Link>
      </div>

      {!isQuote && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="invoice-stats">
          <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total</div><div className="mt-2 font-heading text-2xl font-extrabold tabular">{stats.total}</div><div className="text-xs text-muted-foreground">₹{stats.total_amount.toFixed(0)}</div></div>
          <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Paid</div><div className="mt-2 font-heading text-2xl font-extrabold tabular text-emerald-600">{stats.paid}</div><div className="text-xs text-muted-foreground">₹{stats.paid_amount.toFixed(0)}</div></div>
          <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Unpaid</div><div className="mt-2 font-heading text-2xl font-extrabold tabular text-red-600">{stats.unpaid}</div></div>
          <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Partial</div><div className="mt-2 font-heading text-2xl font-extrabold tabular text-amber-600">{stats.partial}</div></div>
          <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Overdue</div><div className="mt-2 font-heading text-2xl font-extrabold tabular text-red-700">{stats.overdue}</div></div>
        </div>
      )}

      <div className="card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="input pl-9" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search invoice #, client name or mobile…" data-testid="inv-search" />
        </div>
        {!isQuote && (
          <select className="input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="inv-status-filter">
            <option value="">All statuses</option>
            {["Paid", "Unpaid", "Partially Paid"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button className="btn btn-primary" onClick={load}>Apply</button>
      </div>

      <div className="card overflow-hidden">
        <table className="table-clean">
          <thead><tr><th>Number</th><th>Client</th><th>Mobile</th><th>Date</th><th>Amount</th>{!isQuote && <th>Status</th>}<th className="text-right">Actions</th></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No {kind}s yet</td></tr>}
            {list.map((i) => (
              <tr key={i.id}>
                <td><Link to={`${isQuote ? "/quotations" : "/invoices"}/${i.id}`} className="font-mono font-semibold link-blue">{i.number}</Link></td>
                <td className="font-semibold">{i.client_name}<div className="text-xs text-muted-foreground">{i.client_gstin || ""}</div></td>
                <td className="tabular">{i.client_mobile || "—"}</td>
                <td className="text-xs text-muted-foreground">{i.invoice_date}</td>
                <td className="tabular font-semibold">₹{i.grand_total?.toFixed(2) ?? 0}</td>
                {!isQuote && <td><span className={`badge ${badge(i.payment_status)}`}>{i.payment_status}</span></td>}
                <td className="text-right whitespace-nowrap">
                  <Link to={`${isQuote ? "/quotations" : "/invoices"}/${i.id}`} className="btn btn-ghost !p-2 inline-flex" title="Edit"><Edit3 size={16} /></Link>
                  <button className="btn btn-ghost !p-2" onClick={() => downloadPdf(i)} title="PDF"><Download size={16} /></button>
                  <button className="btn btn-ghost !p-2" onClick={() => whatsappShare(i)} title="WhatsApp"><MessageCircle size={16} /></button>
                  <button className="btn btn-ghost !p-2" onClick={() => emailShare(i)} title="Email"><Printer size={16} /></button>
                  {!isQuote && <button className="btn btn-ghost !p-2" onClick={() => duplicate(i)} title="Duplicate"><Copy size={16} /></button>}
                  {isQuote && <button className="btn btn-outline !py-1 !text-xs" onClick={() => convertToInvoice(i)}>→ Invoice</button>}
                  <button className="btn btn-ghost !p-2 text-red-600" onClick={() => del(i)} title="Delete"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
